import {
  Component, Suspense, lazy, useCallback, useContext, useEffect, useId, useMemo, useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, X } from 'lucide-react';
import { useBackToClose } from '@/app/useAndroidBack';
import { useSession } from '@/core/session';
import { useT } from '@/core/i18n';
import { Card, cn } from './ui';
import { PageLoading } from './Loader';
import { QueryError } from './QueryError';
import { DrillContext, type DrillStack } from './drillContext';
import {
  type DrillTarget, keyOf, mayOpen, pageFor,
} from './drillTarget';

/* =============================================================================
 * Opening a figure without leaving the page you are on
 * -----------------------------------------------------------------------------
 * A payslip says 44.35 overtime hours. To find out which days those were,
 * somebody had to leave the payslip, open the attendance grid, find the month,
 * find the person, open a day, read it, and then find their way back to the
 * payslip they were checking — by which time they had lost the row they were on,
 * the filter they had set and the scroll position they had scrolled to.
 *
 * A panel over the top of the page fixes exactly that. The page underneath is
 * never unmounted, so it keeps its scroll, its filters, its open sections and
 * its half-typed search. Closing the panel is not navigation and there is
 * nothing to come back to; the reader was there the whole time.
 *
 * -----------------------------------------------------------------------------
 * WHY IT IS A STACK
 * -----------------------------------------------------------------------------
 * The panels are drillable themselves. Open a day from a payslip, see the site
 * it was charged to, open the site — and closing that has to put the day back,
 * not dump the reader on the payslip. One panel is rendered, the top one, and
 * closing pops it. Three levels deep behaves the way three levels deep should.
 *
 * The same key twice does not stack: opening the day you already have open is a
 * no-op rather than a second identical panel over the first.
 *
 * -----------------------------------------------------------------------------
 * AND WHY A PANEL IS NEVER THE ONLY WAY THERE
 * -----------------------------------------------------------------------------
 * Every panel carries a link to the page it summarises. A panel answers the
 * question in front of somebody; the page is where they go to change something.
 * Trapping a reader in a summary because the summary is convenient is the fault
 * this replaces, in the other direction.
 * ========================================================================== */

/* Lazily loaded, every one. These are pulled in by the pages that link *to*
   them, which is nearly every page in the application — bundling the attendance
   detail, the advances ledger and the payslip into the entry chunk would put
   them on a field phone that opens none of them. */
const AttendanceDayPanel = lazy(() => import('./panels/AttendanceDayPanel'));
const EmployeePanel = lazy(() => import('./panels/EmployeePanel'));
const AdvancesPanel = lazy(() => import('./panels/AdvancesPanel'));
const ProjectPanel = lazy(() => import('./panels/ProjectPanel'));
const LeavePanel = lazy(() => import('./panels/LeavePanel'));

function panelFor(target: DrillTarget): ReactNode {
  switch (target.kind) {
    case 'attendanceDay':
      return <AttendanceDayPanel employeeId={target.employeeId} workDate={target.workDate} />;
    case 'employee':
      return <EmployeePanel employeeId={target.employeeId} />;
    case 'advances':
      return <AdvancesPanel employeeId={target.employeeId} />;
    case 'project':
      return <ProjectPanel projectId={target.projectId} />;
    case 'leave':
      return <LeavePanel leaveId={target.leaveId} />;
    default:
      return null;
  }
}

/* -------------------------------------------------------------------------- */

export function DrillProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<DrillTarget[]>([]);

  const open = useCallback((target: DrillTarget) => {
    setStack((current) => (
      /* The same thing twice is the same thing. Somebody double-tapping a cell
         on a phone should not have to close two identical panels. */
      current.length > 0 && keyOf(current[current.length - 1]!) === keyOf(target)
        ? current
        : [...current, target]
    ));
  }, []);

  const close = useCallback(() => setStack((current) => current.slice(0, -1)), []);
  const closeAll = useCallback(() => setStack([]), []);

  const value = useMemo<DrillStack>(
    () => ({
      open, close, closeAll, depth: stack.length, top: stack[stack.length - 1] ?? null,
    }),
    [open, close, closeAll, stack],
  );

  /* Children, and nothing else. What draws the panel is mounted separately and
     somewhere else on purpose — see DrillHost. */
  return <DrillContext.Provider value={value}>{children}</DrillContext.Provider>;
}

export function useDrill(): DrillStack {
  const ctx = useContext(DrillContext);
  if (!ctx) throw new Error('useDrill must be used inside <DrillProvider>');
  return ctx;
}

/* =============================================================================
 * A panel that breaks must break alone
 * -----------------------------------------------------------------------------
 * The whole reason a panel exists is that the reader does not lose their place.
 * A panel that throws and takes the payslip underneath it down with it fails at
 * the one thing it was for.
 *
 * There is no error boundary anywhere above this in the application, so until
 * this was here a panel that threw took the entire screen, not just itself.
 *
 * Keyed by the target at the call site, so opening something else after a
 * failure gets a fresh attempt rather than the last one's wreckage.
 * ========================================================================== */
class PanelBoundary extends Component<{ children: ReactNode }, { error: unknown }> {
  state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown): { error: unknown } {
    return { error };
  }

  render(): ReactNode {
    if (this.state.error) return <QueryError error={this.state.error} />;
    return this.props.children;
  }
}

/* =============================================================================
 * Where a panel is drawn — and why that is not where its state lives
 * -----------------------------------------------------------------------------
 * The stack is held above the router, so that a route change cannot empty it
 * and a page navigating cannot unmount a panel sitting over it.
 *
 * This is the opposite. It draws a `<Link>` to the full page, and every panel
 * it renders draws more of them. `<Link>` calls `useHref()`, which does not
 * degrade when there is no router above it — it throws. A throw with no error
 * boundary between it and the root unmounts the whole application, which is
 * precisely what happened: clicking any figure, on any screen, in any role,
 * replaced the entire page with a blank one.
 *
 * So the host is mounted inside NativeShell — the router's own root element,
 * above every page and never unmounted by navigation. State outside the router,
 * drawing inside it, joined by the context rather than by props.
 * ========================================================================== */
export function DrillHost() {
  const t = useT();
  const titleId = useId();
  const { top: target, close: onClose, depth } = useDrill();

  /* Android's back button pops one panel rather than navigating out from under
     the page — the same rule every dialog in this application follows. It is a
     last-in-first-out stack of dismissers, so a panel opened over a dialog is
     asked first and the dialog is left alone. */
  useBackToClose(depth > 0, onClose);

  /* Escape, on a desk, and this is the half that was missing.
     -------------------------------------------------------------------------
     A payslip is itself a dialog, and Dialog listens for Escape on the document
     for as long as it is mounted. So a panel opened from a payslip — which is
     the flow this whole mechanism was built for — met an Escape key that closed
     the payslip *underneath* it and left the panel standing over nothing.
     useBackToClose does not help: it is the Android button and returns early on
     the web.
     
     Capture phase, and stopImmediatePropagation rather than stopPropagation.
     Both listeners sit on the document, and stopPropagation only stops the
     event travelling to *other* nodes — every remaining listener on this one
     still runs, including the dialog's. Only stopImmediatePropagation ends it
     here, which is what "the topmost layer answers" means. */
  useEffect(() => {
    if (depth === 0) return undefined;

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      onClose();
    };

    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [depth, onClose]);

  if (!target) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      /* Above z-50, which is what every dialog in this application uses. It
         happens to work on DOM order alone — the host renders after the router
         — and an accident is not a rule. A panel is opened *from* a dialog and
         has to sit over it. */
      className="fixed inset-0 z-[60] flex items-end sm:items-start sm:justify-center sm:p-8 overflow-y-auto"
    >
      <button
        type="button"
        aria-label={t('ef.close')}
        onClick={onClose}
        className="fixed inset-0 bg-black/45 backdrop-enter"
      />

      <Card
        padded={false}
        className={cn(
          'relative w-full sheet-enter',
          'max-h-safe-sheet overflow-y-auto rounded-b-none',
          'sm:my-auto sm:max-h-[calc(100dvh-4rem)] sm:rounded-card sm:max-w-2xl',
        )}
      >
        <div className="flex items-center justify-between gap-4 px-4 sm:px-5 h-14 border-b border-line sticky top-0 bg-surface z-10">
          <h2 id={titleId} className="text-[15px] font-bold tracking-tight truncate min-w-0">
            {t(`drill.${target.kind}` as Parameters<typeof t>[0])}
            {'name' in target && target.name ? ` · ${target.name}` : ''}
          </h2>

          <div className="flex items-center gap-1 shrink-0">
            {/* The way out of the summary and into the page. Closes the whole
                stack on the way, because arriving somewhere with three panels
                still stacked over it is not arriving. */}
            <Link
              to={pageFor(target)}
              onClick={onClose}
              className="flex items-center gap-1 text-[12px] font-medium text-brand hover:underline px-2 py-1.5 rounded-card"
            >
              {t('drill.openFully')}
              <ArrowUpRight size={13} />
            </Link>

            <button
              type="button"
              onClick={onClose}
              aria-label={t('ef.close')}
              className="p-2 -me-2 rounded-card text-subtle hover:text-text hover:bg-surface-2 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="px-4 pt-4 pb-safe-plus-5 sm:px-5 sm:pt-5 sm:pb-5">
          <PanelBoundary key={keyOf(target)}>
            <Suspense fallback={<PageLoading />}>{panelFor(target)}</Suspense>
          </PanelBoundary>
        </div>
      </Card>
    </div>
  );
}

/* =============================================================================
 * The figure itself
 * -----------------------------------------------------------------------------
 * Wraps a cell, a name, an amount — anything that stands for something a reader
 * might want to look into.
 *
 * Renders a button when they may open it and the bare children when they may
 * not, with no styling to say a link was withheld. A site supervisor looking at
 * a shared table sees plain text where a payroll officer sees a link, and
 * nothing on the page tells them there was a difference.
 * ========================================================================== */
export function Drill({
  target, children, className, title,
}: {
  target: DrillTarget;
  children: ReactNode;
  className?: string;
  /** Tooltip. Defaults to the panel's own name. */
  title?: string;
}) {
  const { can, profile } = useSession();
  const { open } = useDrill();
  const t = useT();

  const allowed = mayOpen(target, can, profile?.employeeId ?? null);
  if (!allowed) return <>{children}</>;

  return (
    <button
      type="button"
      title={title ?? t('drill.inspect')}
      onClick={(event) => {
        /* A drill inside a clickable row must not also trigger the row. */
        event.stopPropagation();
        open(target);
      }}
      className={cn(
        'text-start underline decoration-dotted decoration-line underline-offset-[3px]',
        'hover:decoration-brand hover:text-brand transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 rounded-[3px]',
        className,
      )}
    >
      {children}
    </button>
  );
}
